import { useState, useEffect, useRef, useCallback, type MouseEvent as ReactMouseEvent } from 'react';
import { trackEvent } from '../../utils/analytics';
import { useParams, Navigate, useNavigate, useLocation } from 'react-router-dom';
import { useTranslation } from 'react-i18next';
import Joyride, { type CallBackProps } from 'react-joyride';
import { useTourState } from '../../hooks/useTourState';
import { useBreadcrumb } from '../../hooks/useBreadcrumb';
import { profileOrigin } from '../profile/profile-origin';
import { useHideAppFooter } from '../../contexts/app-footer-context';
import { Card, CardContent } from '../ui/card';
import { Skeleton } from '../ui/skeleton';
import { Button } from '../ui/button';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '../ui/select';
import { useToast } from '../ui/use-toast';
import { AlertTriangle, Archive, ChevronsUpDown, MapPinOff } from 'lucide-react';
import { Dialog, DialogContent } from '../ui/dialog';
import { climbsService } from '../../services/climbs';
import { gymsService } from '../../services/gyms';
import { competitionsService } from '../../services/competitions';
import { cragsService } from '../../services/crags';
import { devicesService } from '../../services/devices';
import { useFilteredClimbsList } from '../../hooks/useClimbFilters';
import { useAuth } from '../../hooks/useAuth';
import ClimbNavigation from '../climbs/ClimbNavigation';
import BoardClimbsListDrawer from '../climbs/BoardClimbsListDrawer';
import ClimbsListDrawer from '../climbs/ClimbsListDrawer';
import { climbListPath } from '../climbs/climb-list-path';
import ClimbAdminActions from '../climbs/ClimbAdminActions';
import { isClimbAuthor } from '../climbs/useClimbAdminActions';
import ClimbSendsList from '../sends/ClimbSendsList';
import { ClimbVideoCarousel } from '../climbs/ClimbVideoCarousel';
import { ClimbCoverImage } from '../climbs/ClimbCoverImage';
import { ClimbPhotoPrompt } from '../climbs/ClimbPhotoPrompt';
import { CragViewSwitch } from '../crags/CragModelStage';
import { CragRouteDetails } from '../crags/CragRouteDetails';
import { CragRouteModelCover } from '../crags/CragRouteModelCover';
import { useCragRouteModel } from '../crags/useCragRouteModel';
import { wantsPhoto } from '../climbs/missing-photo';
import CircuitPlayer from '../climbs/board-grid/CircuitPlayer';
import { useBoardLighting } from '../climbs/board-grid/useBoardLighting';
import CircuitPlayerBar from '../climbs/CircuitPlayerBar';
import { useCircuitRun } from '../climbs/circuit-run';
import { photoHoldRole } from '../climbs/photo-hold-roles';
import { HoldsOverlay } from '../climbs/HoldsOverlay';
import { RouteLineOverlay } from '../climbs/RouteLineOverlay';
import { RouteLineModeToggleButton } from '../climbs/RouteLineModeToggleButton';
import { routeLineDisplay, useRouteLineDisplayMode } from '../climbs/route-line-display-mode';
import { hasCorridor, isDrawableLine } from '../climbs/route-line';
import { HoldsModeToggleButton } from '../climbs/HoldsModeToggleButton';
import { useHoldsDisplayMode } from '../climbs/holds-display-mode';
import { climbPhotoPaint, useClimbPhotoEffect } from '../climbs/climb-photo-effect';
import { ShareClimbDialog } from '../climbs/share/ShareClimbDialog';
import { isShareDialogPath, shareAdvancedPath, shareDialogPath, type ShareNavigationState } from '../climbs/share/share-navigation';
import { ClimbProfileFab } from '../climbs/ClimbProfileFab';
import SendTrophyBadge from '../climbs/SendTrophyBadge';
import { logDateFloor } from '../climb-log/log-date-floor';
import { climbTitle } from '../../utils/climb-title';
import type { Climb, Hold } from '../../types/climb';
import type { Gym } from '../../types/gym';
import type { Crag } from '../../types/crag';
import type { Competition } from '../../types/competition';
import type { ClimbLog } from '../../types/climb-log';
import type { Board } from '../../types/device';

/**
 * What a climb with nothing circled on it hands the run. One array for all of
 * them, because a fresh `[]` every render is a fresh identity every render — and
 * the run would rebuild its clock on each one.
 */
const EMPTY_HOLDS: Hold[] = [];

export default function ClimbProfile() {
  const { gymSlug, climbSlug, videoId, boardId } = useParams<{ gymSlug: string; climbSlug: string; videoId?: string; boardId?: string }>();
  const location = useLocation();
  const navigate = useNavigate();
  const { t } = useTranslation();
  const { toast } = useToast();
  const { user } = useAuth();
  // A climb is its picture and what was done on it; the app's footer under the
  // sends is one more screen to scroll past on a phone, and says nothing about it.
  useHideAppFooter();
  const navStateClimbRef = useRef((location.state as { climb?: Climb } | null)?.climb ?? null);
  const [climb, setClimb] = useState<Climb | null>(navStateClimbRef.current);
  const isCompetition = location.pathname.startsWith('/competitions/');
  const isCrag = location.pathname.startsWith('/crags/');
  // Base climb URL without any trailing /video/:id or /share (used for share + the video viewer routes).
  const climbPath = location.pathname.replace(/\/(video\/[^/]+|share)\/?$/, '');
  // `…/share` is this page with the share dialog open over it, so that the
  // address can be handed to somebody and open the dialog for them too.
  const isShareOpen = isShareDialogPath(location.pathname);
  const shareState = (location.state as ShareNavigationState | null) ?? {};

  const [gym, setGym] = useState<Gym | null>(null);
  const [crag, setCrag] = useState<Crag | null>(null);
  // A route drawn on the crag's 3D model opens on it, like the crag's page, with the
  // photograph a switch away (`?view=photo`). A model that fails hands back the photo.
  const routeModel = useCragRouteModel(isCrag ? climb : null, crag, gymSlug);
  const [failedModelUrl, setFailedModelUrl] = useState<string | null>(null);
  const modelAvailable = !!routeModel && routeModel.url !== failedModelUrl;
  const showingModel = modelAvailable && new URLSearchParams(location.search).get('view') !== 'photo';
  const setCoverView = (view: 'model' | 'photo') => {
    const next = new URLSearchParams(location.search);
    if (view === 'photo') next.set('view', 'photo');
    else next.delete('view');
    // The state too: it is how the page knows which list it was opened from.
    navigate({ search: next.toString() }, { replace: true, state: location.state });
  };
  const [competition, setCompetition] = useState<Competition | null>(null);
  // The board this climb is viewed through (from the URL). Its own photo and
  // calibration drive the grid — a global climb shows the gym's Kilter photo.
  const [board, setBoard] = useState<Board | null>(null);
  // How this wall is lit — the gym's device, or this phone's own Bluetooth on a
  // board with no device on it. The lightbulb and the circuit player share it,
  // so a circuit walks the link the button already opened.
  const boardLighting = useBoardLighting(gymSlug, board, !!user);
  // In flight while the "light the LEDs" command is on its way to the wall.
  const [lighting, setLighting] = useState(false);
  // Which hold a circuit run is on, so the board photo points at the same one
  // the wall does. Null when nothing is playing.
  const [circuitHold, setCircuitHold] = useState<number | null>(null);
  // And the same, for a boulder whose circuit is circled on a photo.
  const [photoCircuitHold, setPhotoCircuitHold] = useState<number | null>(null);
  const [loading, setLoading] = useState(!navStateClimbRef.current);
  const [isImageModalOpen, setIsImageModalOpen] = useState(false);
  const [modalImageDims, setModalImageDims] = useState<{ width: number; height: number } | null>(null);
  // Click-to-zoom in the enlarged image. The origin (percent of the clicked
  // point) is kept while zooming out so the scale animates back to that point.
  const [modalZoom, setModalZoom] = useState({ zoomed: false, originX: 50, originY: 50 });
  const { mode: holdsMode, cycle: cycleHoldsMode } = useHoldsDisplayMode();
  const { mode: lineMode, cycle: cycleLineMode } = useRouteLineDisplayMode();
  // How this reader has asked for a wall with a climb on it to be shown. The
  // cover makes the same call: it is the same climb, so the enlarged picture
  // cannot say something else about it.
  const photoEffect = useClimbPhotoEffect(climb);
  const photoPaint = climbPhotoPaint(photoEffect);
  // A route drawn as a line answers to the line's own toggle, and any holds the
  // setter also circled come and go with it — see ClimbCoverImage, which makes
  // the same call for the cover.
  const hasRouteLine = isDrawableLine(climb?.line);
  const modalLineDisplay = routeLineDisplay(lineMode, hasCorridor(climb?.line));
  const modalHoldsMode = hasRouteLine ? (modalLineDisplay.line ? 'all' : 'none') : holdsMode;

  /**
   * A boulder circled on a photo can be a circuit too, and then it is walked hold
   * by hold exactly as a board problem is — the same run and the same bar (see
   * `useCircuitRun`). What differs is where the run is shown: a board lights its
   * wall, a photograph dims down to the hold in hand and flashes it.
   *
   * The run lives on the page rather than inside the player because it is watched
   * in two places at once — the cover and the enlarged picture — and two runs
   * would be two clocks disagreeing about where the climber is.
   */
  const photoCircuit = useCircuitRun({
    holds: climb?.holds ?? EMPTY_HOLDS,
    roleOf: photoHoldRole,
    onEnter: setPhotoCircuitHold,
    onStop: () => setPhotoCircuitHold(null),
    onFinish: () => user && setAddTopTrigger((k) => k + 1),
  });
  // Only a photo boulder that says it is one: a board problem has a player of its
  // own, which has a wall to light as well.
  const photoCircuitPlayable =
    !!climb && climb.type !== 'BoardClimb' && !!climb.circuit && photoCircuit.walkable;
  const [hasFilteredClimbs, setHasFilteredClimbs] = useState(false);
  // The list the climb was opened from, drawn from its name in the breadcrumb.
  // A board problem's is asked of the board (BoardClimbsListDrawer); the rest
  // read the list saved on the way in, as it stood.
  const [listOpen, setListOpen] = useState(false);
  const [savedList, setSavedList] = useState<Climb[]>([]);
  const [sendsRefreshKey, setSendsRefreshKey] = useState(0);
  const [addTopTrigger, setAddTopTrigger] = useState(0);
  const [addTryTrigger, setAddTryTrigger] = useState(0);
  const [addVideoTrigger, setAddVideoTrigger] = useState(0);
  const [editSendTrigger, setEditSendTrigger] = useState(0);
  const [openRateTrigger, setOpenRateTrigger] = useState(0);
  const [userClimbLog, setUserClimbLog] = useState<ClimbLog | null>(null);

  // Climb profile tour (first visit)
  const { run: runClimbTour, complete: completeClimbTour } = useTourState(
    'tour_climb_profile_seen',
    !!user && !loading
  );

  // First-top tour: triggers once when topped transitions false → true
  const [runFirstTopTour, setRunFirstTopTour] = useState(false);
  const isInitialClimbLogLoadRef = useRef(true);

  // Reset initial-load flag when climb changes
  useEffect(() => {
    isInitialClimbLogLoadRef.current = true;
  }, [climbSlug]);

  // Detect first-top transition in this session
  useEffect(() => {
    if (!user || !climbSlug) return;
    if (userClimbLog === null) return; // not loaded yet

    if (isInitialClimbLogLoadRef.current) {
      isInitialClimbLogLoadRef.current = false;
      return; // skip initial load
    }

    if (userClimbLog.topped) {
      const seen = localStorage.getItem('tour_first_top_seen');
      if (!seen) {
        setTimeout(() => setRunFirstTopTour(true), 600);
      }
    }
  }, [userClimbLog, user, climbSlug]);

  const climbTourSteps = [
    {
      target: '[data-testid="cover-rating"]',
      title: t('tour.climbProfile.rateTitle'),
      content: t('tour.climbProfile.rateContent'),
      disableBeacon: true,
    },
    {
      target: '[data-testid="cover-share-button"]',
      title: t('tour.climbProfile.shareTitle'),
      content: t('tour.climbProfile.shareContent'),
      disableBeacon: true,
    },
  ];

  const firstTopTourSteps = [
    {
      target: '[data-tour="share-climb-button"]',
      title: t('tour.firstTop.shareTitle'),
      content: t('tour.firstTop.shareContent'),
      disableBeacon: true,
    },
  ];

  const handleClimbTourCallback = (data: CallBackProps) => {
    if (data.status === 'finished' || data.status === 'skipped') {
      completeClimbTour();
    }
  };

  const handleFirstTopTourCallback = (data: CallBackProps) => {
    if (data.status === 'finished' || data.status === 'skipped') {
      localStorage.setItem('tour_first_top_seen', 'true');
      setRunFirstTopTour(false);
    }
  };

  const tourLocale = {
    back: t('tour.back'),
    close: t('tour.close'),
    last: t('tour.last'),
    next: t('tour.next'),
    skip: t('tour.skip'),
  };

  const tourStyles = {
    options: {
      primaryColor: 'hsl(var(--primary))',
      zIndex: 10000,
    },
  };

  // The list is keyed by the gym for board climbs (a global board climb has no
  // location, and the board tab saved its list under the gym's id).
  const swipeLocationId = climb?.location?.id ?? gym?.id;

  // Check if there are filtered climbs in session storage
  const { getFilteredClimbs, saveFilteredClimbs, isNavigatingFromFilter, clearNavigationFlag } =
    useFilteredClimbsList(swipeLocationId);

  // Guard so this effect only consumes the navigation flag once per mount, even in React strict mode
  // where effects are double-invoked. Without this, optimistic render (climb non-null on mount) would
  // cause the first invocation to clear the flag and the second to see it gone → hasFilteredClimbs=false.
  const hasCheckedFilterRef = useRef(false);

  useEffect(() => {
    if (swipeLocationId && !hasCheckedFilterRef.current) {
      hasCheckedFilterRef.current = true;
      const isFromFilter = isNavigatingFromFilter();
      if (isFromFilter) {
        const { climbs } = getFilteredClimbs();
        setHasFilteredClimbs(climbs.length > 0);
        clearNavigationFlag();
      } else {
        setHasFilteredClimbs(false);
      }
    }
  }, [swipeLocationId, getFilteredClimbs, isNavigatingFromFilter, clearNavigationFlag]);

  useEffect(() => {
    if (!gymSlug || !climbSlug) return;

    let isMounted = true;
    const fetchData = async () => {
      try {
        if (!navStateClimbRef.current) setLoading(true);

        const climbPromise = climbsService.getClimbBySlug(climbSlug);
        const locationPromise = isCompetition
          ? competitionsService.getCompetitionBySlug(gymSlug)
          : isCrag
          ? cragsService.getCragBySlug(gymSlug)
          : gymsService.getGymBySlug(gymSlug);

        const [climbData, locationData] = await Promise.allSettled([
          climbPromise,
          locationPromise
        ]);

        if (!isMounted) return;

        if (climbData.status === 'fulfilled') {
          setClimb(climbData.value);
          trackEvent('climb_viewed', { climb_slug: climbSlug, climb_type: climbData.value.type?.toLowerCase() });
        } else if (!navStateClimbRef.current) {
          setClimb(null);
          throw new Error('Failed to fetch climb');
        }

        if (locationData.status === 'fulfilled') {
          if (isCompetition) {
            setCompetition(locationData.value as Competition);
          } else if (isCrag) {
            setCrag(locationData.value as Crag);
          } else {
            setGym(locationData.value as Gym);
          }
        }
      } catch (error) {
        if (!isMounted) return;
        console.error('Failed to fetch climb profile:', error);
        toast({
          title: t('climbProfile.failedToLoad'),
          description: error instanceof Error ? error.message : t('errors.unexpectedError'),
          variant: 'destructive',
        });
        setClimb(null);
      } finally {
        if (isMounted) setLoading(false);
      }
    };

    fetchData();
    return () => {
      isMounted = false;
    };
  }, [gymSlug, climbSlug, isCompetition, isCrag, t, toast]);

  // Load the board this climb is viewed through — for its photo, calibration and
  // breadcrumb name. Prefer the boardId in the URL; otherwise resolve it from the
  // climb (its own board for a gym climb, the gym's board of that type for a
  // global one) so the /boulders/:slug fallback still aligns the grid.
  const boardClimb = climb?.type === 'BoardClimb' ? climb : null;
  useEffect(() => {
    if (!gymSlug || (!boardId && !boardClimb)) {
      setBoard(null);
      return;
    }
    let cancelled = false;
    devicesService.listGymBoards(gymSlug)
      .then((boards) => {
        if (cancelled) return;
        let found: Board | undefined;
        if (boardId) {
          found = boards.find((b) => b.id === boardId);
        } else if (boardClimb) {
          found = boardClimb.board_id
            ? boards.find((b) => b.id === boardClimb.board_id)
            : boards.find((b) => b.board_type.id === boardClimb.board_type_id);
        }
        setBoard(found ?? null);
      })
      .catch(() => {
        if (!cancelled) setBoard(null);
      });
    return () => {
      cancelled = true;
    };
  }, [gymSlug, boardId, boardClimb]);

  const refreshClimb = async () => {
    if (!climbSlug) return;
    try {
      const updated = await climbsService.getClimbBySlug(climbSlug);
      setClimb(updated);
    } catch {
      // ignore
    }
  };

  // Reset dialog triggers when climb changes to prevent auto-opening dialogs
  useEffect(() => {
    setAddTopTrigger(0);
    setAddTryTrigger(0);
    setEditSendTrigger(0);
    setOpenRateTrigger(0);
  }, [climbSlug]);

  const handleModalClose = (open: boolean) => {
    if (!open) setModalZoom((z) => ({ ...z, zoomed: false }));
    setIsImageModalOpen(open);
  };

  /**
   * Starts a photo circuit — and opens the picture it is walked on.
   *
   * Walking a circuit is looking at which hold it is pointing at, and the cover
   * is a strip of a photograph: the run would begin flashing a hold too small to
   * find. So the enlarged picture opens with it, where the same bar and the same
   * flashing hold are waiting. Started from in there, this is already true and
   * nothing moves.
   */
  const startPhotoCircuit = () => {
    setModalZoom((z) => ({ ...z, zoomed: false }));
    setIsImageModalOpen(true);
    photoCircuit.begin();
  };

  // Toggle click-to-zoom centered on the clicked point of the enlarged image.
  const handleModalImageZoomClick = (e: ReactMouseEvent<HTMLImageElement>) => {
    if (modalZoom.zoomed) {
      setModalZoom((z) => ({ ...z, zoomed: false }));
      return;
    }
    const rect = e.currentTarget.getBoundingClientRect();
    setModalZoom({
      zoomed: true,
      originX: ((e.clientX - rect.left) / rect.width) * 100,
      originY: ((e.clientY - rect.top) / rect.height) * 100,
    });
  };

  const handleGoBack = () => {
    // Check if there's a saved return path from user-profile
    const returnPath = climbSlug ? sessionStorage.getItem(`climb_return_path_${climbSlug}`) : null;
    if (returnPath) {
      if (climbSlug) {
        sessionStorage.removeItem(`climb_return_path_${climbSlug}`);
      }
      navigate(returnPath);
    } else {
      navigate(-1);
    }
  };

  const handleLocationClick = () => {
    if (isCompetition && competition?.slug) {
      navigate(`/competitions/${competition.slug}`);
    } else if (isCrag && crag?.slug) {
      navigate(`/crags/${crag.slug}`);
    } else if (gym?.slug) {
      navigate(`/gyms/${gym.slug}`);
    }
  };

  const handleShare = () => {
    navigate(shareDialogPath(climbPath), { state: { openedInApp: true } satisfies ShareNavigationState });
  };

  const closeShare = () => {
    if (shareState.openedInApp) navigate(-1);
    else navigate(climbPath, { replace: true });
  };

  const openShareAdvanced = () => {
    navigate(shareAdvancedPath(climbPath), {
      state: { presetVideoUrl: shareState.presetVideoUrl } satisfies ShareNavigationState,
    });
  };

  // Lights the problem's holds on the wall. On a board a device drives, the
  // command is relayed over MQTT and success only means it left the server —
  // hence the "sent" wording rather than "the LEDs are on". On a board this
  // phone lights itself the first press also opens the Bluetooth chooser, which
  // is why nothing may be awaited before the call: the browser only opens it
  // inside the click.
  const handleLight = async () => {
    if (!climb || !gymSlug || !board || lighting) return;

    setLighting(true);
    try {
      const lit = await boardLighting.light(climb.id);
      // The climber closed the chooser without picking a board: they changed
      // their mind, and there is nothing to report.
      if (!lit) return;

      toast({
        title: t('climbs.board.lightSent'),
        description: t('climbs.board.lightSentDescription', { board: board.name }),
        duration: 3000,
      });
    } catch (error) {
      console.error('Failed to light the board:', error);
      toast({
        title: t('climbs.board.lightError'),
        description: error instanceof Error ? error.message : t('errors.unexpectedError'),
        variant: 'destructive',
      });
    } finally {
      setLighting(false);
    }
  };

  const handleReportRemoved = async () => {
    if (!climb) return;
    if (!window.confirm(t('climbProfile.reportRemoved.confirm', { name: climbTitle(climb) }))) return;

    try {
      await climbsService.reportRemoved(climb.id);
      toast({
        title: t('climbProfile.reportRemoved.successTitle'),
        description: t('climbProfile.reportRemoved.successDescription'),
        duration: 4000,
      });
    } catch (error) {
      console.error('Failed to report climb as removed:', error);
      toast({
        title: t('climbProfile.reportRemoved.errorTitle'),
        description: t('climbProfile.reportRemoved.errorDescription'),
        variant: 'destructive',
      });
    }
  };

  // The saved list is read when the drawer opens: the swipe may have moved
  // through it since the page was opened.
  const openList = useCallback(() => {
    setSavedList(getFilteredClimbs().climbs);
    setListOpen(true);
  }, [getFilteredClimbs]);

  const pickFromList = (picked: Climb, list: Climb[], index: number) => {
    setListOpen(false);
    if (!gymSlug || picked.slug === climbSlug) return;
    // The list picked from becomes the one the swipe walks, so the climbs
    // either side of this one are the list's neighbours.
    saveFilteredClimbs(list, index);
    clearNavigationFlag();
    setHasFilteredClimbs(true);
    if (swipeLocationId) sessionStorage.setItem(`last_viewed_climb_${swipeLocationId}`, picked.slug);
    navigate(
      climbListPath(picked, gymSlug, {
        boardId: board?.id,
        locationType: isCompetition ? 'competition' : isCrag ? 'crag' : 'gym',
      }),
      { replace: true, state: { climb: picked } }
    );
  };

  // A video opened from the feed carries { fromFeed: true } in history state.
  const openedFromFeed = (location.state as { fromFeed?: boolean } | null)?.fromFeed === true;
  // A climb opened from a profile's grid carries that profile, which then
  // leads the breadcrumb as the way back to it.
  const fromProfile = profileOrigin(location.state);

  useBreadcrumb(
    () => {
      const locationName = (isCompetition
        ? competition?.name
        : isCrag
        ? crag?.name
        : gym?.name) ?? climb?.location?.name;
      const locationLogo = (isCompetition
        ? competition?.logo_small_url || competition?.logo_url
        : isCrag
        ? crag?.logo_small_url || crag?.logo_url
        : gym?.logo_small_url || gym?.logo_url) ?? climb?.location?.logo_small_url ?? undefined;

      // Video opened from the feed: navbar is "DivePeaks → back to the feed" / climb name.
      if (videoId && openedFromFeed && climb) {
        return {
          onBack: () => navigate(-1),
          onHomeLogo: () => navigate(-1),
          segments: [{ label: climbTitle(climb), onClick: () => navigate(climbPath) }],
          loading,
        };
      }

      // Opened from a profile's grid: the profile (the way back to it) → climb
      // name. The gym and the board are left out — the climber is the context.
      if (fromProfile && climb) {
        return {
          onBack: handleGoBack,
          hideHomeLogo: true,
          segments: [
            {
              label: fromProfile.name,
              avatarUrl: fromProfile.avatarUrl ?? '',
              onClick: () => navigate(`/users/${fromProfile.slug}`),
            },
            { label: climbTitle(climb), onClick: videoId ? () => navigate(climbPath) : undefined },
          ],
          loading,
        };
      }

      // The climb's own name, last. Opened from a list, it opens that list to
      // jump to another climb in it; opened any other way there is no list to
      // show, and the name is only a name.
      const climbSegment = (current: Climb) =>
        hasFilteredClimbs
          ? {
              label: climbTitle(current),
              onClick: openList,
              trailingIcon: (
                <ChevronsUpDown
                  className="h-4 w-4 shrink-0 text-muted-foreground"
                  aria-hidden="true"
                  data-testid="climbs-list-trigger"
                />
              ),
            }
          : { label: climbTitle(current) };

      // Board climbs: Gym logo (back to the board's tab) → climb name. No
      // board in between: the gym already leads back to it, and the name gets
      // the room.
      if (climb && climb.type === 'BoardClimb' && board && gymSlug) {
        return {
          onBack: handleGoBack,
          hideHomeLogo: true,
          segments: [
            {
              label: locationName ?? gym?.name ?? '',
              avatarUrl: locationLogo,
              onClick: () => navigate(`/gyms/${gymSlug}?tab=board&board=${board.id}`),
            },
            climbSegment(climb),
          ],
          loading,
        };
      }

      return {
        onBack: handleGoBack,
        hideHomeLogo: true,
        segments: climb && locationName
          ? [
              {
                label: locationName,
                avatarUrl: locationLogo,
                onClick: handleLocationClick,
              },
              // On the video page the climb name simply closes the fullscreen viewer
              // (the climb is already loaded — we just drop the /video/:id segment).
              videoId ? { label: climbTitle(climb), onClick: () => navigate(climbPath) } : climbSegment(climb),
            ]
          : [],
        loading,
      };
    },
    [
      videoId,
      openedFromFeed,
      fromProfile?.slug,
      fromProfile?.name,
      fromProfile?.avatarUrl,
      climbPath,
      navigate,
      climb?.name,
      climb?.type,
      climb?.location?.name,
      climb?.location?.logo_small_url,
      boardId,
      gymSlug,
      board?.id,
      hasFilteredClimbs,
      openList,
      gym?.name,
      gym?.logo_url,
      gym?.logo_small_url,
      competition?.name,
      competition?.logo_url,
      competition?.logo_small_url,
      crag?.name,
      crag?.logo_url,
      crag?.logo_small_url,
      loading,
      isCompetition,
      isCrag,
    ]
  );

  if (!gymSlug || !climbSlug) {
    return <Navigate to="/not-found" replace />;
  }

  if (loading) {
    return (
      <div className="space-y-1 -mt-3">
        {/* Full-bleed cover skeleton */}
        <div className="-mx-4 sm:-mx-6">
          <div className="relative w-full h-[45dvh] md:h-[55dvh] bg-muted overflow-hidden">
            <Skeleton className="w-full h-full rounded-none" />
            {/* Top: name + share buttons */}
            <div className="absolute top-0 left-0 right-0 flex items-start justify-between px-3 py-2">
              <Skeleton className="h-7 w-48 bg-white/20" />
              <div className="flex gap-2">
                <Skeleton className="h-7 w-7 rounded-full bg-white/20" />
                <Skeleton className="h-7 w-7 rounded-full bg-white/20" />
              </div>
            </div>
            {/* Bottom: rating */}
            <div className="absolute bottom-2 left-2">
              <Skeleton className="h-6 w-24 rounded-md bg-white/20" />
            </div>
          </div>
          {/* Info row: grade · setter · trophy */}
          <div className="flex items-center justify-between px-3 py-2 border-b">
            <Skeleton className="h-6 w-16 rounded-full" />
            <Skeleton className="h-5 w-24" />
            <Skeleton className="h-7 w-16 rounded" />
          </div>
        </div>

        {/* Sends list skeleton */}
        <div className="max-w-4xl mx-auto px-4 md:px-6 pt-3 space-y-3">
          {[1, 2, 3].map((i) => (
            <div key={i} className="flex items-center gap-3">
              <Skeleton className="h-9 w-9 rounded-full flex-shrink-0" />
              <div className="flex-1 space-y-1.5">
                <Skeleton className="h-4 w-32" />
                <Skeleton className="h-3 w-48" />
              </div>
            </div>
          ))}
        </div>
      </div>
    );
  }

  if (!climb) {
    return (
      <div className="max-w-4xl mx-auto space-y-1">
        <Card>
          <CardContent className="pt-6 text-center">
            <h1 className="text-2xl font-bold text-muted-foreground">
              {t('climbProfile.notFound')}
            </h1>
          </CardContent>
        </Card>
      </div>
    );
  }

  // On the /video/:id route the video replaces the whole climb-profile view — only
  // the navbar breadcrumb stays. Rendering nothing else means there's no page to
  // scroll behind the fullscreen video.
  if (videoId) {
    return (
      <ClimbVideoCarousel
        climbSlug={climbSlug!}
        climbName={climbTitle(climb)}
        videosCount={climb.videos_count ?? 0}
        climbPath={climbPath}
      />
    );
  }

  return (
    // No `space-y-1` here, unlike the loading state: Joyride renders two empty
    // divs at the top of this tree, and the 4px it would put on the second one
    // collapses straight out through them into this element's own margin,
    // eating a third of the `-mt-3` that cancels main's padding. The cover
    // would then start 4px below the navbar and creep up to it on the first
    // scroll. What it spaced — cover to content — is under the cover anyway.
    <div className="-mt-3">
      <Joyride
        steps={climbTourSteps}
        run={runClimbTour}
        continuous
        showProgress
        showSkipButton
        scrollToFirstStep
        callback={handleClimbTourCallback}
        locale={tourLocale}
        styles={tourStyles}
      />
      <Joyride
        steps={firstTopTourSteps}
        run={runFirstTopTour}
        continuous
        showSkipButton
        callback={handleFirstTopTourCallback}
        locale={tourLocale}
        styles={tourStyles}
      />

      {/* The list the climb was opened from, from its name in the
          breadcrumb: a jump to another without going back to the list and
          finding the place again. */}
      {hasFilteredClimbs &&
        (climb.type === 'BoardClimb' ? (
          board && (
            <BoardClimbsListDrawer
              open={listOpen}
              onOpenChange={setListOpen}
              gymSlug={gymSlug}
              board={board}
              currentSlug={climbSlug}
              onPick={pickFromList}
            />
          )
        ) : (
          <ClimbsListDrawer
            open={listOpen}
            onOpenChange={setListOpen}
            title={t('climbs.openedFromList.title')}
            description={t('climbs.openedFromList.description')}
            emptyText={t('climbs.openedFromList.empty')}
            climbs={savedList}
            currentSlug={climbSlug}
            onPick={pickFromList}
          />
        ))}

      {hasFilteredClimbs && (
        <div className="max-w-4xl mx-auto">
          <ClimbNavigation
            currentClimbSlug={climbSlug}
            gymSlug={gymSlug}
            boardId={boardId}
            locationId={swipeLocationId}
            locationType={isCompetition ? 'competition' : isCrag ? 'crag' : 'gym'}
          />
        </div>
      )}

      {/* Full-bleed cover (image + info row + admin actions) — break out of the
          App container padding. It is the page's header rather than its first
          screenful: it stays put under the navbar and the sends scroll away
          under the staff bar, which is why the picture is a share of the screen
          rather than a fixed height — a hero tall enough to fill it would leave
          nothing to scroll.

          It sticks at 65px — the navbar's `h-16` plus its 1px border — which is
          exactly where it already sits at rest, so it lands without travelling
          and without leaving daylight underneath the navbar on the way.

          A board gets a taller allowance than a photo on a phone, where the two
          amount to the same thing: it is drawn whole rather than cropped, so at
          45dvh it would sit narrower than the screen — the 62dvh is what a
          nearly-square board photo needs to reach the full width. */}
      <div className="sticky top-[65px] z-30 -mx-4 bg-background sm:-mx-6 [--board-height:62dvh] [--cover-height:45dvh] md:[--board-height:55dvh] md:[--cover-height:55dvh]">
        <ClimbCoverImage
          climb={climb}
          board={board}
          // A press on the picture opens the cover out to the whole photograph
          // instead of lifting it into a dialog: the wall around the climb and
          // how high the top is are the rest of the page the reader is on, not
          // a view of their own. Pressing it again gives the cover back.
          expandable
          // The share button is out for now; its place goes to the effect the
          // photograph is shown with, on a climb made off a scan.
          photoEffectToggle
          frameContent={
            showingModel && routeModel ? (
              <CragRouteModelCover
                climb={climb}
                url={routeModel.url}
                byteSize={routeModel.byteSize}
                sectorView={routeModel.sectorView ?? null}
                onFailed={() => {
                  setFailedModelUrl(routeModel.url);
                  toast({ title: t('cragProfile.model3d.failed') });
                }}
              />
            ) : undefined
          }
          frameControls={
            modelAvailable ? <CragViewSwitch view={showingModel ? 'model' : 'photo'} onChange={setCoverView} /> : undefined
          }
          onRate={() => setOpenRateTrigger(k => k + 1)}
          userAuthenticated={!!user}
          // A climb the gym entered as a line in a list: where its photograph
          // would be, the way to take one. The reader is standing in front of the
          // wall, which is the only place the picture can come from.
          photoPrompt={wantsPhoto(climb) ? <ClimbPhotoPrompt climb={climb} /> : undefined}
          className="w-full h-[--cover-height]"
          maxBoardHeight="var(--board-height)"
          holdColorOverlay={climb.hold_color || undefined}
          // A board with a device on it, or — where the browser has Bluetooth —
          // one this phone can light itself. Neither, and there is no lightbulb.
          onLight={boardLighting.transport ? handleLight : undefined}
          // The first press on an unpaired wall opens the chooser, so it says
          // so; once a board has been picked it says which one, since the
          // chooser lists every radio in the gym and picking the wrong one is
          // the one mistake worth being able to see.
          lightLabel={
            boardLighting.transport !== 'local'
              ? undefined
              : boardLighting.connected && boardLighting.boardName
                ? t('climbs.board.bluetooth.lightOnBoard', { board: boardLighting.boardName })
                : t('climbs.board.bluetooth.connectAndLight')
          }
          lighting={lighting}
          currentHold={climb.type === 'BoardClimb' ? circuitHold : photoCircuitHold}
          // A circuit is climbed hold by hold, so it gets a player between the
          // photo and the info row. Reaching the top asks for the send.
          imageFooter={
            climb.type === 'BoardClimb' && climb.circuit && board && gymSlug ? (
              <CircuitPlayer
                gymSlug={gymSlug}
                boardId={board.id}
                climbId={climb.id}
                holds={climb.board_holds ?? []}
                lighting={boardLighting}
                onCurrentChange={setCircuitHold}
                onFinish={() => user && setAddTopTrigger(k => k + 1)}
              />
            ) : photoCircuitPlayable ? (
              <CircuitPlayerBar
                run={photoCircuit}
                currentHand={
                  photoCircuit.current === null
                    ? null
                    : (climb.holds?.[photoCircuit.current]?.hand ?? null)
                }
                onStart={startPhotoCircuit}
              />
            ) : undefined
          }
          infoRowTrailing={
            user ? (
              <SendTrophyBadge
                climbSlug={climbSlug!}
                difficulty={climb.difficulty}
                minDate={logDateFloor(climb)}
                climbType={climb.type === 'BoardClimb' ? 'Boulder' : climb.type}
                dateScope={gymSlug}
                onClimbLogChange={setUserClimbLog}
                onSendsRefresh={() => setSendsRefreshKey(k => k + 1)}
                onRatingSaved={refreshClimb}
                addTopTrigger={addTopTrigger}
                addTryTrigger={addTryTrigger}
                editSendTrigger={editSendTrigger}
                openRateTrigger={openRateTrigger}
              />
            ) : null
          }
          adminActions={(() => {
            if (!user || !climb.location?.id) return undefined;
            const currentUserStaff = user.staff?.find(staff => staff.gym_slug === gymSlug);
            const isStaffMember = currentUserStaff?.status === 'confirmed' &&
              (currentUserStaff?.staff_roles.includes('admin') || currentUserStaff?.staff_roles.includes('editor'));
            return isStaffMember || isClimbAuthor(climb, user) ? (
              <ClimbAdminActions
                climb={climb}
                canReview={!!isStaffMember}
                onClimbUpdated={(updatedClimb) => setClimb(updatedClimb)}
                onClimbDeleted={() => refreshClimb()}
              />
            ) : undefined;
          })()}
        />
      </div>

      {/* Constrained text content */}
      <div className="max-w-4xl mx-auto px-4 md:px-6 space-y-1">

        {/* What the crag's topo says about the route. Crags only: on a gym's route
            the length, the bolts and the types are not written anywhere. */}
        {isCrag && <CragRouteDetails climb={climb} onPage className="pt-3" />}

        {/* Board climb inclination — a select to switch angle when the problem
            exists at several (collapsible types), else a plain label. */}
        {climb.type === 'BoardClimb' && climb.collapsible_inclinations && (climb.inclination_variants?.length ?? 0) > 0 ? (
          (() => {
            const angles = [
              { slug: climb.slug, inclination: climb.inclination, difficulty: climb.difficulty },
              ...(climb.inclination_variants ?? []),
            ].sort((a, b) => (a.inclination ?? 0) - (b.inclination ?? 0));
            return (
              <div className="flex items-center gap-2 pt-3">
                <span className="text-sm text-muted-foreground">{t('climbs.board.inclination')}</span>
                <Select
                  value={climb.slug}
                  onValueChange={(slug) => {
                    if (slug !== climb.slug && board && gymSlug) {
                      navigate(`/gyms/${gymSlug}/boards/${board.id}/climbs/${slug}`);
                    }
                  }}
                >
                  <SelectTrigger className="w-auto min-w-32">
                    <SelectValue />
                  </SelectTrigger>
                  <SelectContent>
                    {angles.map((angle) => (
                      <SelectItem key={angle.slug} value={angle.slug}>
                        {angle.inclination}° · {angle.difficulty ?? '—'}
                      </SelectItem>
                    ))}
                  </SelectContent>
                </Select>
              </div>
            );
          })()
        ) : (
          climb.type === 'BoardClimb' && climb.inclination != null && (
            <p className="pt-3 text-sm text-muted-foreground">
              {t('climbs.board.inclinationLabel', { degrees: climb.inclination })}
            </p>
          )
        )}

        {/* Archived with its board's old photo: drawn on that wall, which is
            gone, and closed to new attempts (docs/SPRAY_WALL_SCAN_PLAN.md). */}
        {climb.archived && (
          <div
            className="bg-muted border rounded-lg p-3 flex items-center gap-2 text-muted-foreground mt-4"
            data-testid="climb-archived-banner"
          >
            <Archive className="h-4 w-4 flex-shrink-0" />
            <span className="text-sm font-medium">{t('climbProfile.archivedBanner')}</span>
          </div>
        )}

        {/* Discarded climb banner */}
        {climb.discarded_at && !climb.archived && (
          <div className="bg-amber-50 dark:bg-amber-950 border border-amber-200 dark:border-amber-800 rounded-lg p-3 flex items-center gap-2 text-amber-800 dark:text-amber-200 mt-4">
            <AlertTriangle className="h-4 w-4 flex-shrink-0" />
            <span className="text-sm font-medium">
              {t('climbProfile.discardedBanner')}
            </span>
          </div>
        )}

        {/* Video carousel (small square previews) above the sends list; adding one is on the FAB */}
        <ClimbVideoCarousel
          climbSlug={climbSlug!}
          climbName={climbTitle(climb)}
          videosCount={climb.videos_count ?? 0}
          climbPath={climbPath}
          addVideoTrigger={addVideoTrigger}
        />

        {/* Sends list - all sends for this climb */}
        <div className="space-y-4 pt-3">
          <ClimbSendsList
            climbSlug={climbSlug!}
            refreshKey={sendsRefreshKey}
            onAddSend={() => setAddTopTrigger(k => k + 1)}
            initialAscentLogId={new URLSearchParams(location.search).get('ascent_log') ?? undefined}
            initialCommentId={new URLSearchParams(location.search).get('comment') ?? undefined}
          />
        </div>

        {/* Report the climb as no longer present — subtle action at the page bottom.
            Bottom-nav clearance is handled by the app shell below the footer, so only
            a small gap is needed here. */}
        {user && !climb.discarded_at && (
          <div className="flex justify-center pt-10 pb-2">
            <Button
              variant="ghost"
              size="sm"
              onClick={handleReportRemoved}
              className="text-muted-foreground hover:text-amber-700"
            >
              <MapPinOff className="h-4 w-4 mr-2" />
              {t('climbProfile.reportRemoved.button')}
            </Button>
          </div>
        )}
      </div>

      {/* Floating Action Button (always visible) */}
      <ClimbProfileFab
        isAuthenticated={!!user}
        hasToppedClimb={!!userClimbLog?.topped}
        canLog={!climb.archived}
        onAddTry={() => setAddTryTrigger(k => k + 1)}
        onAddTop={() => setAddTopTrigger(k => k + 1)}
        onShare={handleShare}
        // Logged in: the upload dialog. Logged out: the share dialog, to make one from the phone.
        onAddVideo={() => (user ? setAddVideoTrigger(k => k + 1) : handleShare())}
        onAddVariant={
          climb.type === 'BoardClimb' && climb.collapsible_inclinations && board && gymSlug && !climb.archived
            ? () => navigate(`/gyms/${gymSlug}/boards/${board.id}/climbs/new?variantOf=${climb.slug}`)
            : undefined
        }
      />

      {/* Image Modal */}
      <Dialog open={isImageModalOpen} onOpenChange={handleModalClose}>
        <DialogContent className="max-w-4xl p-0 overflow-hidden bg-transparent border-none">
          {climb && climb.image_url && (
            <div className="relative">
              {/* Image + holds overlay zoom together; the toggle and name bar stay fixed. */}
              <div className="overflow-hidden">
                <div
                  className="relative transition-transform duration-300 ease-out"
                  style={{
                    transform: modalZoom.zoomed ? 'scale(2.5)' : 'none',
                    transformOrigin: `${modalZoom.originX}% ${modalZoom.originY}%`,
                  }}
                >
                  <img
                    src={climb.image_url}
                    alt={climbTitle(climb)}
                    className={`w-full h-auto max-h-[80vh] object-contain ${modalZoom.zoomed ? 'cursor-zoom-out' : 'cursor-zoom-in'}`}
                    style={{ filter: photoPaint.wall }}
                    onLoad={(e) => setModalImageDims({ width: e.currentTarget.naturalWidth, height: e.currentTarget.naturalHeight })}
                    onClick={handleModalImageZoomClick}
                  />
                  <HoldsOverlay
                    holds={climb.holds}
                    naturalWidth={modalImageDims?.width ?? null}
                    naturalHeight={modalImageDims?.height ?? null}
                    objectFit="contain"
                    mode={modalHoldsMode}
                    // Enlarged is where the effect earns its keep: the picture
                    // is big enough to read the plastic, and the colour (or the
                    // light) is only where the climb is. The cover says the same
                    // thing smaller.
                    effect={photoEffect}
                    imageSrc={climb.image_url}
                    // The order the holds are climbed in, which is the whole of
                    // what a circuit is — enlarged along with the picture, since
                    // the overlay zooms inside the same layer, and flashing the
                    // hold the run is on wherever the run is being watched from.
                    circuit={!!climb.circuit}
                    currentHold={photoCircuitHold}
                  />
                  {hasRouteLine && (modalLineDisplay.line || modalLineDisplay.focus) && (
                    <RouteLineOverlay
                      line={climb.line}
                      naturalWidth={modalImageDims?.width ?? null}
                      naturalHeight={modalImageDims?.height ?? null}
                      objectFit="contain"
                      showLine={modalLineDisplay.line}
                      focusOutside={modalLineDisplay.focus}
                    />
                  )}
                </div>
              </div>
              {hasRouteLine ? (
                <RouteLineModeToggleButton
                  mode={lineMode}
                  onToggle={cycleLineMode}
                  positionClassName="bottom-16"
                  testId="modal-route-line-toggle"
                />
              ) : climb.holds && climb.holds.length > 0 ? (
                <HoldsModeToggleButton
                  mode={holdsMode}
                  onToggle={cycleHoldsMode}
                  positionClassName="bottom-16"
                  testId="modal-holds-toggle"
                />
              ) : null}
              <div className="absolute bottom-0 left-0 right-0 px-4 py-3 bg-gradient-to-t from-black/70 to-transparent">
                <h2 className="text-white font-bold text-lg">{climbTitle(climb)}</h2>
              </div>
            </div>
          )}

          {/* The circuit is walked from here too, and from the same run: the
              climber enlarges the picture precisely to follow the holds it
              flashes, and a player left behind on the page under it would be a
              player they cannot reach. */}
          {photoCircuitPlayable && (
            <div className="rounded-b-lg bg-background pb-3">
              <CircuitPlayerBar
                run={photoCircuit}
                currentHand={
                  photoCircuit.current === null
                    ? null
                    : (climb?.holds?.[photoCircuit.current]?.hand ?? null)
                }
                onStart={startPhotoCircuit}
              />
            </div>
          )}
        </DialogContent>
      </Dialog>

      <ShareClimbDialog
        open={isShareOpen}
        onOpenChange={(open) => { if (!open) closeShare(); }}
        climb={climb}
        board={board}
        climbPath={climbPath}
        onOpenAdvanced={openShareAdvanced}
      />
    </div>
  );
}
